const VOLUME_CONTEXT = {
  Appendices: {
    frame:
      'Reference shelf. Use this to decode terms, symbols, and case studies before entering the deeper volumes.',
    lab:
      'Appendix energy: not glamorous, but without it the reactor labels are just spicy alphabet soup.',
  },
  Core: {
    frame:
      'The operating manifesto for the whole library: memes as measurable information organisms, not just jokes with captions.',
    lab:
      'Core doctrine: the joke is the wrapper, the payload is the thing sneaking past the bouncer.',
  },
  Diagrams: {
    frame:
      'Visual models for turning abstract memetic theory into something you can point at during a lab argument.',
    lab:
      'Diagram rule: if it cannot survive being drawn badly on a napkin, it is probably not dank enough yet.',
  },
  Papers: {
    frame:
      'Longer-form arguments that turn the fake discipline into a usable framework for analysis and design.',
    lab:
      'Whitepaper cosplay is allowed here, but only if the idea can still bench-press a real meme.',
  },
  'Volume 1 Foundations': {
    frame:
      'Foundational definitions: what a meme is, what state it occupies, and why memetic energy moves people.',
    lab:
      'Foundation work is the concrete under the meme accelerator. Boring until it prevents the whole tunnel from exploding.',
  },
  'Volume 2 Meme Mathematics': {
    frame:
      'Quantitative language for fitness, reproduction, momentum, acceleration, calculus, and the sacred coefficient of dankness.',
    lab:
      'Numbers do not make a meme funny, but they help explain why one bad JPEG outruns a million-dollar campaign.',
  },
  'Volume 3 Topology': {
    frame:
      'How social space shapes diffusion: clusters, bridges, manifolds, communities, and the shortest path to group chat detonation.',
    lab:
      'Topology is where a meme learns that “the internet” is actually a maze of tiny rooms yelling at each other.',
  },
  'Volume 4 Cringe Mechanics': {
    frame:
      'Cringe as a usable carrier wave: too little is invisible, too much melts the payload, the right amount sticks.',
    lab:
      'Cringe is radioactive. Handle with gloves, irony layers, and plausible deniability.',
  },
  'Volume 5 Thermodynamics': {
    frame:
      'Entropy, slop collapse, institutionalization, and the way cultural heat dissipates through overproduction.',
    lab:
      'Thermodynamics explains why yesterday’s elite format becomes tomorrow’s LinkedIn carousel tragedy.',
  },
  'Volume 6 Genetics': {
    frame:
      'Memes as lineages: genomes, mutation, crossover, drift, and evolution under social selection pressure.',
    lab:
      'Meme genetics is how the lab tracks whether the child format inherited genius, brainrot, or both.',
  },
  'Volume 7 Fluid Dynamics': {
    frame:
      'Attention as flow: pressure, velocity, vortices, half-life, and branching through cultural pipes.',
    lab:
      'Hydrodynamics is the part where we admit the timeline is a sewer system with surprisingly elegant turbulence.',
  },
  'Volume 8 Immune Systems': {
    frame:
      'Defense mechanisms that communities use against foreign, low-status, over-sincere, or suspiciously corporate memes.',
    lab:
      'Memetic immune systems are why “hello fellow kids” remains a federally recognized cringe event.',
  },
  'Volume 9 Engineering': {
    frame:
      'Applied memetics: payload wrappers, narrative design, authenticity, and the dangerous craft of building transmissible ideas.',
    lab:
      'Engineering is where the lab coat gets stains. If theory cannot ship, it goes back in the containment jar.',
  },
  'Volume 10 Unified Theory': {
    frame:
      'The high-energy physics cosplay layer: unified equations, carriers, particles, and a shared model of memetic force.',
    lab:
      'Unified theory is what happens when CERN envy meets a doge macro and nobody stops the whiteboard session.',
  },
}

const TOPIC_OVERRIDES = {
  readme: {
    plain:
      'This is the front door: memes are treated as living information systems with fitness, mutation, defenses, lifecycles, and measurable social context.',
    matters:
      'It tells readers how to hold the tone: the discipline is fake enough to be fun and consistent enough to become useful.',
    collider:
      'Use the README as the rubric for deciding whether a new article belongs in the library: does it explain replication, mutation, diffusion, payload, or defense?',
  },
  project: {
    plain:
      'The charter names the mission: build a coherent meme-science framework that can survive both a whiteboard and a group chat.',
    matters:
      'A silly wrapper without a charter becomes slop. A charter keeps the absurdity pointed at a real intellectual target.',
    collider:
      'Turn the milestone list into public feature prompts: scoring worksheets, diagrams, slide decks, and platform defaults.',
  },
  'volume-1-foundations-01-what-is-a-meme': {
    plain:
      'A meme is a replicating package of information: image, phrase, ritual, format, identity signal, or payload wrapper.',
    matters:
      'This broad definition keeps the library from confusing “meme” with “captioned picture.” The picture is only one possible vessel.',
    collider:
      'When testing a meme, identify its payload, wrapper, replication behavior, mutation surface, and target immune system.',
  },
  'volume-1-foundations-02-meme-state-vector': {
    plain:
      'A meme state vector describes the current condition of a meme: recognition, emotional charge, irony layer, remixability, timing, and social location.',
    matters:
      'The same format can be dead, rising, cringe, evergreen, or weaponized depending on state. State prevents lazy one-number scoring.',
    collider:
      'Add a “state vector” worksheet to the generator later: what audience, what emotion, what timing, what mutation path?',
  },
  'volume-1-foundations-03-meme-energy': {
    plain:
      'Meme energy is the stored capacity of an idea to make people notice, repeat, remix, argue, identify, or react.',
    matters:
      'Energy separates inert content from content with motion. A technically polished artifact can still have zero memetic voltage.',
    collider:
      'Score a draft by asking what reaction it stores: laughter, recognition, anger, pride, curiosity, shame, or pure “send this now.”',
  },
  'volume-2-meme-mathematics-mathematics-of-dankness': {
    plain:
      'Dankness is modeled as an emergent score from recognition, novelty, timing, emotional charge, remixability, and controlled cringe.',
    matters:
      'It gives MDMC a serious/silly vocabulary for comparing memes without pretending humor is a solved equation.',
    collider:
      'Expose a future “dankness worksheet” beside the meme generator: recognition, novelty, timing, cringe, slop risk.',
  },
  'volume-4-cringe-mechanics-cringe-as-a-method': {
    plain:
      'Cringe can be deliberately used as a transport layer. It lowers guardrails, invites reaction, and makes the payload memorable.',
    matters:
      'A culture that over-polishes everything becomes sterile. Controlled cringe reintroduces friction, humanity, and shareable imperfection.',
    collider:
      'A/B test polished vs semi-cringe captions. Track which version people actually repeat instead of merely approving.',
  },
  'volume-4-cringe-mechanics-semi-cringe-wrappers': {
    plain:
      'A semi-cringe wrapper is intentionally a little embarrassing, goofy, or low-status so the serious payload can sneak through.',
    matters:
      'It explains the MDMC brand itself: the silly shell protects serious memetic analysis from sounding like corporate sludge.',
    collider:
      'Use semi-cringe as a dial, not a floodgate: enough weirdness to disarm, not enough to make the reader flee.',
  },
  'volume-5-thermodynamics-slop-collapse': {
    plain:
      'Slop collapse happens when a format is copied so much, so lazily, that its signal becomes noise.',
    matters:
      'This is the death mode of every generative-content tool. MDMC should help users avoid becoming another particle in the slop cloud.',
    collider:
      'Add “slop risk” warnings later when a caption is too generic, too derivative, or too obviously machine-shaped.',
  },
  'volume-6-genetics-meme-genome': {
    plain:
      'A meme genome is the reusable structure that survives across variants: template, relationship, rhythm, role assignment, and expected twist.',
    matters:
      'Once you can name the genome, you can mutate the surface without killing the lineage.',
    collider:
      'Treat templates as genomes: list what must stay stable and what users are free to mutate.',
  },
  'volume-7-fluid-dynamics-attention-pressure': {
    plain:
      'Attention pressure is the force created when many people, incentives, events, and platforms push awareness through the same cultural channel.',
    matters:
      'Even a strong meme can fail in a low-pressure environment; even a mediocre meme can fly during a pressure spike.',
    collider:
      'Pair meme generation with current context later: news pressure, platform mood, community saturation, and timing windows.',
  },
  'volume-8-immune-systems-irony-layers': {
    plain:
      'Irony layers let a meme survive hostile inspection by allowing multiple readings: sincere, joking, meta-joking, anti-joking, or ritualized nonsense.',
    matters:
      'Layered irony is both shield and engine. It protects the sharer while expanding who can participate.',
    collider:
      'Give creators an “irony level” choice later: sincere, wink, deep fried, post-ironic, or “the reactor is making noises.”',
  },
  'volume-9-engineering-payload-wrappers': {
    plain:
      'A payload wrapper is the visible container that carries the real idea: joke, template, character, slogan, story, ritual, or image macro.',
    matters:
      'Most campaigns fail because the payload is obvious and the wrapper is boring. Memetic engineering starts with the wrapper.',
    collider:
      'Every generated meme should answer: what is the payload, what is the wrapper, and why would someone carry it forward?',
  },
  'volume-10-unified-theory-unified-equation': {
    plain:
      'The unified equation is a playful attempt to gather fitness, dankness, cringe, diffusion, mutation, and social topology into one model.',
    matters:
      'It gives the library a north-star equation even if every coefficient is provisional and wearing sunglasses.',
    collider:
      'Use it as the future scorecard skeleton: not a truth machine, but a useful set of knobs for thinking clearly.',
  },
  'appendices-doge': {
    plain:
      'Doge is a durable meme because it combines warmth, broken grammar, recognizable image grammar, and unusually high mutation tolerance.',
    matters:
      'It proves that a meme can become language, identity, money, and mascot without losing its core emotional signature.',
    collider:
      'Study Doge when building friendly public-facing MDMC copy: absurd, warm, and instantly legible.',
  },
  'appendices-loss': {
    plain:
      'Loss is a compression meme: recognition survives even when almost all original detail is stripped away.',
    matters:
      'It demonstrates that spatial grammar can become payload. The arrangement itself becomes the joke.',
    collider:
      'Use Loss as the test case for abstraction: how few pixels or roles can remain before recognition fails?',
  },
  'appendices-rickroll': {
    plain:
      'Rickroll is a ritualized bait-and-switch whose payload is benign enough to be repeatedly forgiven.',
    matters:
      'It shows that trust violation can be playful if the punishment is culturally shared and low-cost.',
    collider:
      'Use it as the model for safe surprise: deception without harm, recognition without resentment.',
  },
  'appendices-wojak': {
    plain:
      'Wojak is a container for projected emotional states. Its simplicity makes it mutate into identities, archetypes, and social diagnoses.',
    matters:
      'It is less a character than a reusable emotional socket. That is why it survives endless redraws.',
    collider:
      'When evaluating character templates, ask whether they are specific enough to recognize and empty enough to inhabit.',
  },
}

const RELATED_KEYWORDS = [
  ['cringe', ['volume-4-cringe-mechanics-cringe-as-a-method', 'volume-4-cringe-mechanics-semi-cringe-wrappers']],
  ['dank', ['volume-2-meme-mathematics-mathematics-of-dankness']],
  ['diffusion', ['papers-meme-diffusion-theory', 'volume-3-topology-diffusion']],
  ['genome', ['volume-6-genetics-meme-genome', 'volume-6-genetics-mutation']],
  ['slop', ['volume-5-thermodynamics-slop-collapse']],
  ['payload', ['volume-9-engineering-payload-wrappers']],
  ['immune', ['volume-8-immune-systems-identity-defense', 'volume-8-immune-systems-irony-layers']],
  ['attention', ['volume-7-fluid-dynamics-attention-pressure', 'volume-10-unified-theory-attention-bosons']],
]

function firstSentence(text) {
  const clean = String(text || '')
    .replace(/^#+\s+/gm, '')
    .replace(/\[[^\]]+\]\([^)]+\)/g, '')
    .replace(/[*_`|]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
  const match = clean.match(/^(.{40,260}?[.!?])\s/)
  return match ? match[1] : clean.slice(0, 220)
}

function inferPlain(entry) {
  const volume = VOLUME_CONTEXT[entry.volume]?.frame || 'This topic contributes one working part to the MDMC theory stack.'
  return `${firstSentence(entry.content)} In the library, this sits inside ${entry.volume}: ${volume}`
}

function inferMatters(entry) {
  if (entry.kind === 'diagram') {
    return 'It turns abstract theory into a shared object. If two people can point at the same model, they can argue productively instead of vibes-only jousting.'
  }
  if (entry.kind === 'appendix') {
    return 'It gives the theory a reference handle: examples, vocabulary, and notation make the fake discipline easier to reuse without re-explaining the whole reactor.'
  }
  if (entry.kind === 'paper') {
    return 'It expands a compact idea into an argument, which makes the concept easier to teach, challenge, and turn into product behavior.'
  }
  if (entry.kind === 'volume') {
    return 'It defines a shelf of the canon. Shelves keep the weirdness navigable and prevent every concept from collapsing into one giant slop drawer.'
  }
  return 'It adds a usable mental model for designing, scoring, or interpreting memes instead of merely asking whether something feels funny.'
}

function inferCollider(entry) {
  if (entry.volume.includes('Mathematics')) {
    return 'Use it as a scoring knob: turn the concept into a 1-5 rating and compare two candidate memes before shipping the funnier one.'
  }
  if (entry.volume.includes('Cringe')) {
    return 'Use it as a wrapper test: ask whether the cringe level disarms the audience or makes the payload radioactive.'
  }
  if (entry.volume.includes('Topology')) {
    return 'Use it as an audience-routing test: identify which cluster receives the meme first and which bridge carries it outward.'
  }
  if (entry.volume.includes('Thermodynamics')) {
    return 'Use it as a lifecycle test: decide whether the format is heating up, cooling down, institutionalizing, or collapsing into slop.'
  }
  if (entry.volume.includes('Genetics')) {
    return 'Use it as a mutation test: preserve the recognizable genome while changing one variable at a time.'
  }
  if (entry.volume.includes('Fluid')) {
    return 'Use it as a timing test: estimate attention pressure, velocity, half-life, and whether a branch is worth opening.'
  }
  if (entry.volume.includes('Immune')) {
    return 'Use it as a defense test: predict what the target community will reject, mock, absorb, or ritualize.'
  }
  if (entry.volume.includes('Engineering')) {
    return 'Use it as a build test: define payload, wrapper, audience, mutation path, and failure mode before generating assets.'
  }
  if (entry.volume.includes('Unified')) {
    return 'Use it as a systems test: map the topic to fitness, diffusion, mutation, cringe, attention, and topology.'
  }
  return 'Use it as a lab prompt: turn the idea into one meme, one caption variant, and one reason someone would share it.'
}

function cleanWords(text) {
  return String(text || '')
    .replace(/^#+\s+/gm, '')
    .replace(/\[[^\]]+\]\([^)]+\)/g, '')
    .replace(/[`*_>|]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

function wordCount(text) {
  return cleanWords(text).split(/\s+/).filter(Boolean).length
}

function entrySignals(entry) {
  const title = entry.title
  const volume = entry.volume
  const kind = entry.kind
  const source = cleanWords(entry.content)
  const excerpt = entry.excerpt || firstSentence(entry.content)
  return { title, volume, kind, source, excerpt }
}

function mathFor(entry) {
  const { title, volume, kind } = entrySignals(entry)
  if (volume.includes('Mathematics')) {
    return `Math model: treat "${title}" as a scoring component in D = (R * N * T * E * M) / (1 + S + P), where D is dankness, R is recognizability, N is novelty, T is timing, E is emotional charge, M is mutation potential, S is slop penalty, and P is polish penalty. For this topic, define a local score X_${kind} from 0 to 1, then test whether raising X increases D without also raising S. The practical equation is ΔD = D_after - D_before. If ΔD is positive and the meme remains legible to the target cluster, the concept helped. If ΔD is negative, the math says the reactor generated decorative fog, not usable heat.`
  }
  if (volume.includes('Topology')) {
    return `Math model: place "${title}" on a social graph G = (V, E). Each audience is a node, each trust path is an edge, and diffusion strength is approximated by P_share = β * A * C / (1 + I), where β is baseline transmission probability, A is attention pressure, C is cluster compatibility, and I is immune resistance. A topic is topologically useful when it improves bridge probability between two clusters without destroying local meaning. Measure that as B = P_cross - P_local_decay. Positive B means the meme can travel; negative B means it becomes an in-joke trapped in one room.`
  }
  if (volume.includes('Cringe')) {
    return `Math model: model cringe as a carrier wave C, not a simple defect. Useful cringe lives near C*, the optimal cringe point, where D(C) = PayloadRetention * AttentionGain - RejectionCost. Too little C produces invisible competence; too much C produces evacuation. For "${title}", estimate C on a 0 to 1 scale, then compare it to C* for the audience. The working test is |C - C*| < ε, where ε is tolerance. If the wrapper feels a little dangerous but the payload still arrives, the cringe vector is doing work. If everyone only remembers the awkwardness, C exceeded containment.`
  }
  if (volume.includes('Thermodynamics')) {
    return `Math model: treat the meme as a thermal system with heat H, entropy S, and useful work W. For "${title}", W = H * Coherence - S_slop. Heat is attention, coherence is retained structure, and slop entropy is the loss caused by lazy copies. A format is healthy when dW/dt stays positive across remix cycles. If H rises but coherence falls faster, the system enters slop collapse. If H falls while coherence stabilizes, the meme is cooling into archive value or institutional ritual. The practical question is whether the next variant adds useful work or merely dumps waste heat into the feed.`
  }
  if (volume.includes('Genetics')) {
    return `Math model: represent "${title}" as a genome g = [template, role, rhythm, payload, twist]. Mutation μ changes one gene, crossover χ combines two genomes, and fitness F(g) measures survival after remix. The safe engineering rule is Δg_one: mutate one gene at a time while preserving recognizability R above a threshold τ. If R(g') > τ and F(g') > F(g), the mutation is productive. If recognizability drops below τ, the child meme may be novel but no longer belongs to the lineage. MDMC should track both mutation rate and lineage continuity.`
  }
  if (volume.includes('Fluid')) {
    return `Math model: attention behaves like flow. For "${title}", define velocity v = shares / time, pressure p = audience demand plus event relevance, and half-life h = time until activity falls by 50 percent. Flow through a channel is Q = p * v * b, where b is branching coefficient. If Q is high and h is short, ship fast variants. If Q is low and h is long, build evergreen explainers. If p rises but v stays low, the meme has pressure without a usable outlet. The lab move is to open a better channel or simplify the payload until flow begins.`
  }
  if (volume.includes('Immune')) {
    return `Math model: every audience has an immune threshold I*. For "${title}", rejection probability can be approximated as P_reject = I * O * S / (1 + L), where I is identity defense, O is outsider smell, S is sincerity suspicion, and L is local legitimacy. A meme survives when P_reject stays below the audience threshold while payload clarity remains high. The field test is not “is this true?” but “will this community accept the carrier?” Lower O with native language, lower S with irony layers, and raise L through familiar symbols.`
  }
  if (volume.includes('Engineering')) {
    return `Math model: engineering turns intent into repeatable payload transfer. For "${title}", define Effect = PayloadClarity * WrapperFit * AudienceNeed * RemixEase - FailureRisk. Each factor is scored from 0 to 1 before launch. If any factor is near zero, multiplication kills the effect no matter how strong the others are. This is why a brilliant payload inside a boring wrapper fails, and why a beautiful wrapper with no payload becomes slop. The MDMC build rule is simple: maximize effect, minimize failure risk, and preserve enough weirdness that the object still wants to travel.`
  }
  if (volume.includes('Unified')) {
    return `Math model: the unified approximation is U = F * D * Λ * μ / (1 + C_bad + S), where F is fitness, D is dankness, Λ is network reach or topology eigenvalue, μ is mutation potential, C_bad is destructive cringe, and S is slop. For "${title}", the topic matters if it helps estimate one term or the interaction between terms. The equation is not a truth machine; it is a dashboard. A useful topic makes one gauge less mysterious, tells the operator which knob to turn, and prevents the meme reactor from confusing heat with progress.`
  }
  return `Math model: assign "${title}" a local topic coefficient X from 0 to 1 and plug it into M = F * R * E * T * X / (1 + S), where M is memetic usefulness, F is fitness, R is recognizability, E is emotional charge, T is timing, and S is slop penalty. X measures how well this topic improves the meme being studied. The experiment is comparative: create variant A without the concept and variant B with the concept, then estimate ΔM = M_B - M_A. Positive ΔM means the topic improved transmission or interpretation. Negative ΔM means the lab note sounded smart but did not move the meme.`
}

function expandPlain(entry, seed) {
  const { title, volume, kind, excerpt } = entrySignals(entry)
  return `${seed} In practical MDMC terms, "${title}" is not just a ${kind}; it is a lens for noticing a repeatable behavior in meme life. The source note says: ${excerpt}. The expansion is that this topic should help a reader move from “that feels funny” to “I can name the mechanism.” It belongs to ${volume}, so read it as part of that shelf's larger machine rather than as an isolated joke. A useful reading asks what is being carried, what changes during remix, what the audience recognizes, and what breaks when the concept is pushed too far.`
}

function expandMatters(entry, seed) {
  const { title, volume } = entrySignals(entry)
  const frame = VOLUME_CONTEXT[volume]?.frame || 'This shelf contributes one working subsystem to the larger theory.'
  return `${seed} The reason "${title}" matters is that memetic work fails when creators only optimize surface flavor. Surface flavor is important, but it is the wrapper, not the whole system. This topic gives the lab a name for one hidden variable underneath the wrapper. ${frame} When a concept has a name, it can be compared, tested, improved, and challenged. That is the difference between taste and method. The silly wrapper keeps the idea approachable; the serious core keeps it from dissolving into vibes. If a reader can use this topic to explain why one meme travels and another dies, it has earned its shelf space.`
}

function expandCollider(entry, seed) {
  const { title } = entrySignals(entry)
  return `${seed} In the collider, "${title}" should become an operator prompt, not merely a paragraph. Start with a candidate meme, caption, article hook, or campaign idea. Then ask three questions: what does this topic reveal, what does it hide, and what would change if the topic were ignored? Build two variants: one that deliberately applies the concept and one that avoids it. Compare recognizability, emotional charge, remix ease, likely immune response, and slop risk. If the concept improves the second-order behavior of the meme, keep it. If it only makes the lab notes sound smarter, send it back through the cringe washer.`
}

function expandProtocol(entry) {
  const { title, volume } = entrySignals(entry)
  return `Operator protocol for "${title}": first, summarize the payload in one sentence. Second, identify the wrapper: image, phrase, ritual, graph, character, story, or interface pattern. Third, score recognizability, novelty, timing, emotional charge, remixability, cringe, and slop risk from 0 to 5. Fourth, apply the math model and write down which coefficient this topic changes. Fifth, produce one small artifact that tests the hypothesis: a caption, a diagram, a meme template, a short post, or a landing-page line. Sixth, review the result against ${volume}. The goal is not to worship the formula; the goal is to force clearer thinking before the reactor starts throwing sparks.`
}

function expandLabNote(entry, seed) {
  const { title, volume } = entrySignals(entry)
  const lab = seed || VOLUME_CONTEXT[volume]?.lab || 'Dank lab note unavailable; the intern fed it to the meme reactor.'
  return `${lab} Field caution for "${title}": every useful memetic concept can become its own parody if over-applied. The lab should use this topic like seasoning, not concrete. Too little and the reader learns nothing; too much and the content starts wearing a fake mustache while yelling “I am theory.” Keep the wrapper playful, keep the payload legible, and always ask whether the idea survives contact with an actual audience. If it cannot improve a meme, explain a meme, or prevent slop, it belongs in quarantine until further testing.`
}

function compactThesis(entry, seed) {
  const { title, volume, excerpt } = entrySignals(entry)
  const lead = seed || firstSentence(entry.content)
  return `${lead} The useful version of “${title}” is a way to name one force inside meme behavior, not a license to over-explain the joke. In ${volume}, it helps separate the visible wrapper from the payload, audience, timing, and mutation path. Source kernel: ${excerpt}`
}

function compactMechanism(entry, seed) {
  const { title, volume } = entrySignals(entry)
  const frame = VOLUME_CONTEXT[volume]?.frame || 'This shelf contributes one subsystem to the MDMC theory stack.'
  return `${seed || inferMatters(entry)} Mechanically, “${title}” asks what changes when the meme leaves the creator and enters a social system. ${frame} If the concept cannot improve prediction, design, or diagnosis, it should stay a joke; if it can, it becomes an operator control.`
}

function compactPractice(entry, seed) {
  const { title } = entrySignals(entry)
  return [
    seed || inferCollider(entry),
    `Test “${title}” with two variants: one that deliberately uses the concept and one that ignores it.`,
    'Compare recognizability, emotional charge, remix ease, audience resistance, and slop risk before choosing the winner.',
  ]
}

function mathCardFor(entry) {
  const { title, volume, kind } = entrySignals(entry)
  if (volume.includes('Mathematics')) {
    return {
      title: 'Dankness Score',
      formula: 'D = (R × N × T × E × M) / (1 + S + P)',
      variables: [
        ['D', 'dankness'],
        ['R', 'recognizability'],
        ['N', 'novelty'],
        ['T', 'timing'],
        ['E', 'emotional charge'],
        ['M', 'mutation potential'],
        ['S', 'slop penalty'],
        ['P', 'over-polish penalty'],
      ],
      interpretation: `For “${title}”, raise one signal without raising slop. If ΔD is positive after a variant test, the concept helped.`,
    }
  }
  if (volume.includes('Topology')) {
    return {
      title: 'Network Travel',
      formula: 'Pshare = β × A × C / (1 + I)',
      variables: [
        ['β', 'baseline share rate'],
        ['A', 'attention pressure'],
        ['C', 'cluster fit'],
        ['I', 'immune resistance'],
      ],
      interpretation: 'A meme travels when it fits the first room and still has enough bridge energy to cross into the next one.',
    }
  }
  if (volume.includes('Cringe')) {
    return {
      title: 'Cringe Window',
      formula: 'Useful if |C - C*| < ε',
      variables: [
        ['C', 'actual cringe'],
        ['C*', 'audience-optimal cringe'],
        ['ε', 'tolerance band'],
      ],
      interpretation: 'Cringe works when it disarms attention without making the payload radioactive.',
    }
  }
  if (volume.includes('Thermodynamics')) {
    return {
      title: 'Useful Heat',
      formula: 'W = H × K - Sslop',
      variables: [
        ['W', 'useful memetic work'],
        ['H', 'attention heat'],
        ['K', 'coherence retained'],
        ['Sslop', 'entropy from lazy copies'],
      ],
      interpretation: 'A format is alive when attention creates work instead of dumping more waste heat into the feed.',
    }
  }
  if (volume.includes('Genetics')) {
    return {
      title: 'Lineage Fitness',
      formula: 'F(g′) > F(g) while R(g′) ≥ τ',
      variables: [
        ['g', 'current meme genome'],
        ['g′', 'mutated variant'],
        ['F', 'fitness'],
        ['R', 'recognizability'],
        ['τ', 'lineage threshold'],
      ],
      interpretation: 'A mutation succeeds only if it improves fitness without breaking the recognizability chain.',
    }
  }
  if (volume.includes('Fluid')) {
    return {
      title: 'Attention Flow',
      formula: 'Q = p × v × b',
      variables: [
        ['Q', 'flow through the channel'],
        ['p', 'attention pressure'],
        ['v', 'share velocity'],
        ['b', 'branching coefficient'],
      ],
      interpretation: 'High pressure with low velocity means the idea needs a simpler outlet or a better channel.',
    }
  }
  if (volume.includes('Immune')) {
    return {
      title: 'Rejection Risk',
      formula: 'Preject = I × O × S / (1 + L)',
      variables: [
        ['I', 'identity defense'],
        ['O', 'outsider smell'],
        ['S', 'sincerity suspicion'],
        ['L', 'local legitimacy'],
      ],
      interpretation: 'Lower outsider smell, add local legitimacy, and use irony carefully when an audience is defensive.',
    }
  }
  if (volume.includes('Engineering')) {
    return {
      title: 'Payload Transfer',
      formula: 'Effect = Pc × Wf × An × Re - Fr',
      variables: [
        ['Pc', 'payload clarity'],
        ['Wf', 'wrapper fit'],
        ['An', 'audience need'],
        ['Re', 'remix ease'],
        ['Fr', 'failure risk'],
      ],
      interpretation: 'Strong memetic engineering multiplies fit and clarity, then subtracts the ways the wrapper can betray the payload.',
    }
  }
  if (volume.includes('Unified')) {
    return {
      title: 'Unified Meme Potential',
      formula: 'U = F × D × Λ × μ / (1 + Cbad + S)',
      variables: [
        ['F', 'fitness'],
        ['D', 'dankness'],
        ['Λ', 'network reach'],
        ['μ', 'mutation potential'],
        ['Cbad', 'destructive cringe'],
        ['S', 'slop'],
      ],
      interpretation: 'The equation is a dashboard, not an oracle: it tells the operator which gauge is probably lying.',
    }
  }
  return {
    title: 'Topic Utility',
    formula: `M = F × R × E × T × X${kind} / (1 + S)`,
    variables: [
      ['M', 'memetic usefulness'],
      ['F', 'fitness'],
      ['R', 'recognizability'],
      ['E', 'emotional charge'],
      ['T', 'timing'],
      [`X${kind}`, 'topic-specific contribution'],
      ['S', 'slop penalty'],
    ],
    interpretation: `For “${title}”, the topic earns its place only if it raises usefulness without raising slop.`,
  }
}

function relatedFor(entry) {
  const haystack = `${entry.title} ${entry.path} ${entry.excerpt} ${entry.content}`.toLowerCase()
  const ids = new Set()
  for (const [keyword, related] of RELATED_KEYWORDS) {
    if (haystack.includes(keyword)) {
      related.forEach((id) => {
        if (id !== entry.id) ids.add(id)
      })
    }
  }
  return Array.from(ids).slice(0, 5)
}

export function getPrincipiaCommentary(entry) {
  if (!entry) return null
  const override = TOPIC_OVERRIDES[entry.id] || {}
  const volumeNote = VOLUME_CONTEXT[entry.volume] || {}
  const thesis = compactThesis(entry, override.plain || inferPlain(entry))
  const mechanism = compactMechanism(entry, override.matters || inferMatters(entry))
  const practice = compactPractice(entry, override.collider || inferCollider(entry))
  const math = mathCardFor(entry)
  const labNote = override.labNote || volumeNote.lab || 'Dank lab note unavailable; the intern fed it to the meme reactor.'
  return {
    thesis,
    mechanism,
    practice,
    math,
    labNote,
    relatedIds: override.relatedIds || relatedFor(entry),
  }
}
